<template>
  <el-dialog
    title="编辑采购单"
    :visible.sync="dialogVisible"
    width="920px"
    custom-class="create-purchase-order-dialog"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div class="pay-order-section">
      <div class="section-title">产品明细</div>
      <el-table
        :data="previewList"
        border
        stripe
        size="mini"
        show-summary
        :summary-method="getSummaries"
        v-loading="loading"
        element-loading-text="加载中..."
        max-height="320"
      >
        <el-table-column label="序号" width="50" align="center" prop="index"></el-table-column>
        <el-table-column prop="materialName" label="材料名称" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.materialName || scope.row.productName || "-" }}</template>
        </el-table-column>
        <el-table-column prop="purchaseQuantity" label="采购数量" width="80" align="right"></el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="purchaseAmount" label="采购金额" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.purchaseAmount) }}</template>
        </el-table-column>
        <el-table-column prop="taxRate" label="税率" width="70" align="right">
          <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate != null ? scope.row.taxRate : form.taxRate) }}</template>
        </el-table-column>
        <el-table-column prop="taxAmount" label="税费" width="80" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
        </el-table-column>
        <el-table-column prop="payableAmount" label="应付金额" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.payableAmount) }}</template>
        </el-table-column>
      </el-table>
    </div>

    <div class="pay-order-form">
      <div class="section-title">支付信息</div>
      <div class="form-grid">
        <div class="form-item wide">
          <span class="form-label">采购供应商</span>
          <el-input :value="supplierDisplayName" size="small" disabled></el-input>
          <span class="tax-label">税率：{{ formatTaxRate(form.taxRate) }}</span>
        </div>
        <div class="form-item wide">
          <span class="form-label">运费</span>
          <el-input v-model.number="form.freight" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">平台优惠</span>
          <el-input v-model.number="form.platformDiscount" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">退换货费</span>
          <el-input v-model.number="form.returnExchangeFee" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">其他费用</span>
          <el-input v-model.number="form.otherFee" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">下单平台</span>
          <el-radio-group v-model="form.orderPlatform" size="small">
            <el-radio label="对公">对公</el-radio>
            <el-radio label="平台">平台</el-radio>
          </el-radio-group>
        </div>
        <div class="form-item wide remark-item">
          <span class="form-label">备注</span>
          <el-input v-model="form.remark" type="textarea" :rows="2" size="small" placeholder="请输入备注" maxlength="500" show-word-limit></el-input>
        </div>
      </div>
      <div class="amount-summary">
        <div class="amount-row">
          <span class="amount-label">应付金额</span>
          <span class="amount-value payable">{{ formatMoney(form.payableAmount) }}</span>
        </div>
        <div class="amount-row">
          <span class="amount-label">实付金额</span>
          <el-input v-model.number="form.actualPaidAmount" size="small" type="number" class="amount-input" @input="onActualPaidInput"></el-input>
        </div>
        <div class="amount-row">
          <span class="amount-label">差额</span>
          <span class="amount-value" :class="amountDiffClass">{{ formatAmountDiff(amountDiff) }}</span>
          <span class="amount-tip">（实付金额 - 应付金额）</span>
        </div>
      </div>
      <div class="preview-tip" v-if="orderNo">采购单号：{{ orderNo }}；供应商、产品明细不可修改</div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" size="small" :loading="submitting" :disabled="!orderId" @click="submit">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { requestModules } from "@r";

function createEmptyForm() {
  return {
    supplierId: "",
    supplierName: "",
    freight: 0,
    platformDiscount: 0,
    returnExchangeFee: 0,
    otherFee: 0,
    orderPlatform: "对公",
    taxRate: 0,
    actualPaidAmount: 0,
    payableAmount: 0,
    remark: "",
  };
}

export default {
  name: "EditPurchaseOrderDialog",
  data() {
    return {
      dialogVisible: false,
      loading: false,
      submitting: false,
      orderId: "",
      orderNo: "",
      previewList: [],
      form: createEmptyForm(),
    };
  },
  computed: {
    supplierDisplayName() {
      return this.form.supplierName || this.form.supplierId || "-";
    },
    amountDiff() {
      const actualPaidAmount = parseFloat(this.form.actualPaidAmount) || 0;
      const payableAmount = parseFloat(this.form.payableAmount) || 0;
      return parseFloat((actualPaidAmount - payableAmount).toFixed(2));
    },
    amountDiffClass() {
      if (this.amountDiff > 0) return "positive";
      if (this.amountDiff < 0) return "negative";
      return "zero";
    },
  },
  methods: {
    open(row) {
      const orderId = row && (row.orderId || row._id);
      if (!orderId) {
        this.$message.warning("缺少采购单ID");
        return;
      }
      this.orderId = String(orderId);
      this.orderNo = (row && row.orderNo) || "";
      this.previewList = [];
      this.form = createEmptyForm();
      this.dialogVisible = true;
      this.loadDetail();
    },
    loadDetail() {
      this.loading = true;
      requestModules
        .fetchPurchaseOrderForEdit({ orderId: this.orderId })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "加载采购单失败");
            this.dialogVisible = false;
            return;
          }
          const mdata = res.mdata || {};
          this.orderNo = mdata.orderNo || this.orderNo;
          this.previewList = res.ldata || [];
          this.form = {
            supplierId: mdata.supplierId || "",
            supplierName: mdata.supplierName || "",
            freight: parseFloat(mdata.freight) || 0,
            platformDiscount: parseFloat(mdata.platformDiscount) || 0,
            returnExchangeFee: parseFloat(mdata.returnExchangeFee) || 0,
            otherFee: parseFloat(mdata.otherFee) || 0,
            orderPlatform: mdata.orderPlatform || "对公",
            taxRate: parseFloat(mdata.taxRate) || 0,
            actualPaidAmount: parseFloat(mdata.actualPaidAmount) || 0,
            payableAmount: parseFloat(mdata.payableAmount) || 0,
            remark: mdata.remark || "",
          };
          this.calcTotal();
        })
        .catch(() => {
          this.$message.error("加载采购单失败");
          this.dialogVisible = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    calcTotal() {
      const linePayableAmount = (this.previewList || []).reduce((sum, row) => sum + (Number(row.payableAmount) || 0), 0);
      const freight = parseFloat(this.form.freight) || 0;
      const platformDiscount = parseFloat(this.form.platformDiscount) || 0;
      const returnExchangeFee = parseFloat(this.form.returnExchangeFee) || 0;
      const otherFee = parseFloat(this.form.otherFee) || 0;
      this.form.payableAmount = parseFloat((linePayableAmount + freight - platformDiscount + returnExchangeFee + otherFee).toFixed(2));
    },
    onActualPaidInput() {
      if (this.form.actualPaidAmount === "" || this.form.actualPaidAmount === null) {
        this.form.actualPaidAmount = 0;
      }
    },
    formatMoney(value) {
      if (value === "" || value === null || value === undefined) {
        return "0.00";
      }
      return parseFloat(value).toFixed(2);
    },
    formatTaxRate(value) {
      if (value === "" || value === null || value === undefined) {
        return "0%";
      }
      return (parseFloat(value) * 100).toFixed(0) + "%";
    },
    formatAmountDiff(value) {
      if (value > 0) {
        return "+" + this.formatMoney(value);
      }
      return this.formatMoney(value);
    },
    getSummaries({ columns, data }) {
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "purchaseQuantity") {
          sums[index] = (data || []).reduce((sum, row) => sum + (Number(row.purchaseQuantity) || 0), 0);
          return;
        }
        if (["purchaseAmount", "taxAmount", "payableAmount"].includes(column.property)) {
          sums[index] = this.formatMoney((data || []).reduce((sum, row) => sum + (Number(row[column.property]) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    submit() {
      if (!this.orderId) {
        this.$message.warning("缺少采购单ID");
        return;
      }
      if (this.submitting) return;
      this.calcTotal();
      this.submitting = true;
      requestModules
        .updatePurchaseOrderPayment({
          orderId: this.orderId,
          freight: parseFloat(this.form.freight) || 0,
          platformDiscount: parseFloat(this.form.platformDiscount) || 0,
          returnExchangeFee: parseFloat(this.form.returnExchangeFee) || 0,
          otherFee: parseFloat(this.form.otherFee) || 0,
          orderPlatform: this.form.orderPlatform || "对公",
          actualPaidAmount: parseFloat(this.form.actualPaidAmount) || 0,
          remark: (this.form.remark || "").trim(),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "编辑采购单失败");
            return;
          }
          if (res.mdata && res.mdata.unchanged) {
            this.$message.info(res.message || "未检测到变更");
          } else {
            this.$message.success("编辑成功");
          }
          this.dialogVisible = false;
          this.$emit("success", res);
        })
        .catch(() => {
          this.$message.error("编辑采购单失败");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    handleClose() {
      this.orderId = "";
      this.orderNo = "";
      this.previewList = [];
      this.form = createEmptyForm();
    },
  },
};
</script>

<style lang="scss">
.create-purchase-order-dialog {
  .el-dialog__body {
    padding-top: 10px;
  }

  .pay-order-section {
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
    padding-left: 8px;
    border-left: 3px solid #409eff;
  }

  .pay-order-form {
    padding: 16px;
    background: #f8f9fb;
    border-radius: 6px;
    border: 1px solid #ebeef5;

    .form-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 12px 24px;
      margin-bottom: 16px;
    }

      .form-item {
      display: flex;
      align-items: center;
      min-width: 240px;

      &.wide {
        width: 100%;
      }

      &.remark-item {
        align-items: flex-start;

        .el-textarea {
          flex: 1;
          width: auto;
        }
      }

      .el-select,
      .el-input {
        width: 180px;
      }
    }

    .form-label {
      display: inline-block;
      width: 80px;
      flex-shrink: 0;
      color: #606266;
      font-size: 13px;
    }

    .tax-label {
      margin-left: 12px;
      color: #909399;
      font-size: 12px;
    }

    .amount-summary {
      padding-top: 12px;
      border-top: 1px dashed #dcdfe6;
    }

    .amount-row {
      display: flex;
      align-items: center;
      min-height: 36px;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .amount-label {
      width: 80px;
      flex-shrink: 0;
      color: #606266;
      font-size: 13px;
    }

    .amount-value {
      font-size: 16px;
      font-weight: 600;

      &.payable {
        color: #409eff;
      }

      &.positive {
        color: #67c23a;
      }

      &.negative {
        color: #f56c6c;
      }

      &.zero {
        color: #909399;
      }
    }

    .amount-input {
      width: 180px;
    }

    .amount-tip {
      margin-left: 10px;
      color: #909399;
      font-size: 12px;
    }
  }

  .preview-tip {
    margin-top: 12px;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
